import {
  Component,
  ChangeDetectionStrategy,
  TemplateRef,
  input,
  computed,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';

type BannerVariant = 'informative' | 'warning' | 'error';

interface IBannerVariantConfig {
  classes: string;
  icon: string;
  severity: 'primary' | 'warn' | 'danger';
}
@Component({
  selector: 'ag1-banner',
  templateUrl: './ag1-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule, Bind, Button, NgTemplateOutlet, Tooltip],
})
export class Ag1BannerComponent {
  // Inputs
  variant = input<BannerVariant>('informative'); // used to style the banner
  heading = input<string>(); // optional heading
  message = input.required<string>(); // required message
  isDismissible = input<boolean>(true); // whether the banner can be dismissed
  hasAction = input<boolean>(false); // whether the banner has an action
  actionLabel = input<string>('Action'); // the label for the action button
  actionTemplateRef = input<TemplateRef<any>>(); // pass in a custom action template if you need to deviate from the default button

  // the classes for the banner based on the variant
  private readonly _variantConfig: Record<BannerVariant, IBannerVariantConfig> =
    {
      informative: {
        classes:
          'border-interface-informative-border-default bg-interface-informative-muted-default text-text-informative-muted-default',
        icon: '/assets/icons/general/info.svg',
        severity: 'primary',
      },
      warning: {
        classes:
          'border-interface-warning-border-default bg-interface-warning-muted-default text-text-warning-default',
        icon: '/assets/icons/general/system-feedback-warning.svg',
        severity: 'warn',
      },
      error: {
        classes:
          'border-interface-danger-border-default bg-interface-danger-muted-default text-text-danger-default',
        icon: '/assets/icons/general/system-feedback-warning.svg',
        severity: 'danger',
      },
    };

  // the classes for the banner based on the applied variant
  appliedClasses = computed(() => {
    return (
      this._variantConfig[this.variant()]?.classes ||
      this._variantConfig.informative.classes
    );
  });

  // the icon for the banner based on the applied variant
  appliedIcon = computed(() => {
    return (
      this._variantConfig[this.variant()]?.icon ||
      this._variantConfig.informative.icon
    );
  });

  // the primeNG severity for the banner based on the applied variant
  appliedSeverity = computed(() => {
    return (
      this._variantConfig[this.variant()]?.severity ||
      this._variantConfig.informative.severity
    );
  });

  // Outputs
  dismissEvent = output<void>(); // emit an event when the banner is dismissed
  actionEvent = output<void>(); // emit an event when the banner is actioned

  constructor() {}

  onAction() {
    this.actionEvent.emit();
  }

  onDismiss() {
    this.dismissEvent.emit();
  }
}
